import { startCelebration, stopCelebration } from './celebration';
import { loadQuestions, runQuiz } from './quiz';
import { createInitialState, cloneState, applyMove, applyReineMove, undoReinePickup, opponent, liftPiece } from './state';
import { renderBoard } from './board';
import { rollDice, animateRoll, callOutDice } from './dice';
import { getLegalMoves, getLegalTargetsForPoint, getReinePlacements, checkMotherLoss, checkGameOver, hasAnyLegalMove } from './rules';
import { buildAiTurnStates } from './ai';
import type { GameState, Player, LegalTarget } from './types';

// ─── DOM refs ────────────────────────────────────────────────────────────────
const celebLayer  = document.getElementById('celebration-layer') as HTMLElement;
const svg         = document.getElementById('board-svg') as unknown as SVGSVGElement;
const rollBtn     = document.getElementById('roll-btn') as HTMLButtonElement;
const reineBtn    = document.getElementById('reine-btn') as HTMLButtonElement;
const statusBar   = document.getElementById('status-bar') as HTMLDivElement;
const scoreBar    = document.getElementById('score-bar') as HTMLDivElement;
const diceArea    = document.getElementById('dice-area') as HTMLDivElement;
const controlsEl  = document.getElementById('controls') as HTMLDivElement;
const quizArea    = document.getElementById('quiz-area') as HTMLDivElement;
const boardOverlay = document.getElementById('board-overlay') as HTMLDivElement;

// ─── Game state ──────────────────────────────────────────────────────────────
let state: GameState = createInitialState();
let hasMovedThisTurn = false;

// Expose for debugging / future quiz integration
(window as unknown as Record<string, unknown>).gameState = state;
(window as unknown as Record<string, unknown>).activateReine = () => {
  if (state.phase === 'rolling' || state.phase === 'moving') {
    diceArea.innerHTML = '';
    state = { ...cloneState(state), isReine: true, phase: 'moving', reinePickup: null, legalTargets: [], selection: null };
    startCelebration(celebLayer);
    render();
    setStatus('✨ Reine mode! Pick any piece.');
  }
};

// ─── Render ──────────────────────────────────────────────────────────────────
function render() {
  renderBoard(state, svg, handlePointClick, handleBearOffClick);
  renderDice();
  updateControls();
  scoreBar.textContent = `White: ${state.scores.white} — Black: ${state.scores.black}`;
  (window as unknown as Record<string, unknown>).gameState = state;
}

function renderDice() {
  diceArea.innerHTML = '';
  if (state.dice.length === 0) return;
  state.dice.forEach((val, i) => {
    const div = document.createElement('div');
    div.className = 'die' + (state.diceUsed[i] ? ' used' : '');
    div.textContent = String(val);
    div.id = `die-${i}`;
    diceArea.appendChild(div);
  });
}

function updateControls() {
  const isHumanTurn = state.currentPlayer === 'white';
  const inPlayerPhase = state.phase === 'rolling' || state.phase === 'moving';

  rollBtn.style.display = state.isReine ? 'none' : '';
  rollBtn.disabled = !(isHumanTurn && state.phase === 'rolling');

  const showReine = isHumanTurn && inPlayerPhase && !hasMovedThisTurn;
  reineBtn.style.display = showReine ? '' : 'none';
  reineBtn.disabled = !showReine;
  reineBtn.classList.toggle('active', state.isReine);
}

function setStatus(msg: string) {
  statusBar.textContent = msg;
}

// ─── Click handlers ──────────────────────────────────────────────────────────
function handlePointClick(pt: number) {
  if (state.currentPlayer !== 'white') return;
  if (state.phase !== 'moving') return;

  // ── Reine mode ──
  if (state.isReine) {
    if (state.reinePickup === null) {
      // Pick up a piece
      const ptState = state.points[pt]!;
      if (ptState.topOwner === null && ptState.pinnedOwner === null) return;

      // Determine which piece to lift.
      // When both a top stack and a pinned piece exist, default to the current
      // player's own piece — almost always what they intend (escape or flip the block).
      const liftPinned = ptState.topOwner !== null && ptState.pinnedOwner !== null
        ? ptState.pinnedOwner === state.currentPlayer
        : ptState.topOwner === null; // only pinned exists → must lift it

      const owner = liftPinned ? ptState.pinnedOwner! : ptState.topOwner!;
      const lifted = liftPiece(state, pt, liftPinned);
      const placements = getReinePlacements(lifted, { point: pt, owner, wasPinned: liftPinned });

      state = {
        ...lifted,
        reinePickup: { point: pt, owner, wasPinned: liftPinned },
        legalTargets: placements,
        selection: pt,
      };
      render();
      setStatus(`✨ Piece lifted — ${placements.length} valid placements`);
      return;
    }

    // Place the picked-up piece
    const target = state.legalTargets.find(t => t.to === pt);
    if (!target) {
      // Clicking a non-target: cancel pickup and return the piece to its slot.
      const restored = undoReinePickup(state, state.reinePickup);
      state = { ...restored, reinePickup: null, selection: null, legalTargets: [] };
      render();
      setStatus('✨ Reine mode! Pick any piece.');
      return;
    }

    const newState = applyReineMove(state, state.reinePickup, pt);
    state = { ...newState, selection: null, legalTargets: [] };
    render();
    endHumanTurn();
    return;
  }

  // ── Normal mode ──
  const ptState = state.points[pt]!;

  // If a target was already highlighted and this is a valid destination
  if (state.selection !== null) {
    const target = state.legalTargets.find(t => t.to === pt);
    if (target) {
      executeMove(state.selection, pt, target.dieIndices[0]!);
      return;
    }
  }

  // Select this point if it has our pieces
  if (ptState.topOwner !== 'white') {
    state = { ...cloneState(state), selection: null, legalTargets: [] };
    render();
    return;
  }

  const targets = getLegalTargetsForPoint(state, pt);
  state = { ...cloneState(state), selection: pt, legalTargets: targets };
  render();

  if (targets.length === 0) {
    setStatus('No legal moves from here.');
  } else {
    setStatus(`${targets.length} move${targets.length > 1 ? 's' : ''} available — click destination`);
  }
}

function handleBearOffClick(player: Player) {
  if (state.currentPlayer !== 'white' || state.phase !== 'moving') return;

  // Bear-off target (to === 0)
  if (state.isReine && state.reinePickup !== null) {
    const target = state.legalTargets.find(t => t.to === 0);
    if (!target) return;
    const newState = applyReineMove(state, state.reinePickup, 0);
    state = { ...newState, selection: null, legalTargets: [] };
    render();
    endHumanTurn();
    return;
  }

  if (state.selection !== null) {
    const target = state.legalTargets.find(t => t.to === 0);
    if (!target) return;
    executeMove(state.selection, 0, target.dieIndices[0]!);
  }
}

function executeMove(from: number, to: number, dieIndex: number) {
  hasMovedThisTurn = true;
  let newState = applyMove(state, from, to, dieIndex);

  // Check mother loss
  const motherResult = checkMotherLoss(newState, 'white');
  if (motherResult !== null) {
    newState = { ...newState, motherOutcome: motherResult, phase: 'gameover' };
    state = newState;
    render();
    handleMotherOutcome(motherResult);
    return;
  }

  // Check game over
  const over = checkGameOver(newState);
  if (over) {
    const pts = over.mars ? 2 : 1;
    newState = {
      ...newState,
      winner: over.winner,
      phase: 'gameover',
      scores: {
        ...newState.scores,
        [over.winner]: newState.scores[over.winner] + pts,
      },
    };
    state = newState;
    render();
    setStatus(`🎉 ${over.winner === 'white' ? 'You win' : 'AI wins'}!${over.mars ? ' MARS!' : ''}`);
    rollBtn.disabled = false;
    rollBtn.textContent = 'New Game';
    rollBtn.onclick = newGame;
    return;
  }

  // No dice left or nowhere to go → end turn immediately
  const allUsed = newState.diceUsed.every(Boolean);
  if (allUsed || !hasAnyLegalMove(newState)) {
    state = { ...newState, selection: null, legalTargets: [] };
    render();
    endHumanTurn();
    return;
  }

  // If the piece that just moved (now at `to`) can still move, keep it selected
  // so the player can chain-move it without having to re-click.
  if (to !== 0) {
    const chainTargets = getLegalTargetsForPoint(newState, to);
    if (chainTargets.length > 0) {
      state = { ...newState, selection: to, legalTargets: chainTargets };
      render();
      setStatus('Select destination or click another piece');
      return;
    }
  }

  // Moved piece can't go further — deselect, let the player pick any piece
  state = { ...newState, selection: null, legalTargets: [] };
  render();
  setStatus('Select a piece to move');
}

function endHumanTurn() {
  if (state.isReine) stopCelebration();
  state = {
    ...cloneState(state),
    phase: 'ai',
    currentPlayer: 'black',
    selection: null,
    legalTargets: [],
    isReine: false,
    reinePickup: null,
  };
  reineBtn.classList.remove('active');
  render();
  setStatus('AI is thinking…');
  setTimeout(runAiTurn, 600);
}

// ─── AI turn ─────────────────────────────────────────────────────────────────
async function runAiTurn() {
  // Show AI dice roll animation before computing moves
  const values = rollDice();
  diceArea.innerHTML = '';
  const diceEls: HTMLElement[] = values.map(() => {
    const div = document.createElement('div');
    div.className = 'die';
    div.textContent = '?';
    diceArea.appendChild(div);
    return div;
  });
  await animateRoll(diceEls, values);
  callOutDice(values);
  await delay(500);

  const snapshots = buildAiTurnStates(state, values);
  if (snapshots.length === 0) {
    finishAiTurn(state);
    return;
  }

  // Animate each snapshot with a delay
  for (const snap of snapshots) {
    state = snap;
    render();
    await delay(550);
    if (snap.phase === 'gameover' || snap.motherOutcome !== null) {
      handleAiGameEnd(snap);
      return;
    }
  }

  finishAiTurn(snapshots[snapshots.length - 1]!);
}

function finishAiTurn(finalState: GameState) {
  hasMovedThisTurn = false;
  state = {
    ...cloneState(finalState),
    phase: 'rolling',
    currentPlayer: 'white',
    isReine: false,
    reinePickup: null,
    selection: null,
    legalTargets: [],
    motherOutcome: null,
  };
  render();
  setStatus('Your turn — roll the dice');
}

function handleAiGameEnd(snap: GameState) {
  if (snap.motherOutcome !== null) {
    handleMotherOutcome(snap.motherOutcome);
  } else if (snap.winner !== null) {
    const over = checkGameOver(snap)!;
    const pts = over.mars ? 2 : 1;
    state = {
      ...cloneState(snap),
      scores: {
        ...snap.scores,
        [snap.winner]: snap.scores[snap.winner] + pts,
      },
    };
    render();
    setStatus(`${snap.winner === 'white' ? 'You win' : 'AI wins'}!${over.mars ? ' MARS!' : ''}`);
    scheduleNewGame();
  }
}

function handleMotherOutcome(outcome: import('./types').MotherOutcome) {
  if (outcome === 'tie') {
    setStatus('Both mothers pinned — TIE! Replay.');
    hasMovedThisTurn = false;
    state = createInitialState();
    setTimeout(() => { render(); setStatus('Replay — your turn, roll the dice'); }, 2000);
    return;
  }
  const loser = outcome === 'white-loses' ? 'white' : 'black';
  const winner = loser === 'white' ? 'black' : 'white';
  state = {
    ...cloneState(state),
    phase: 'gameover',
    winner,
    scores: { ...state.scores, [winner]: state.scores[winner] + 2 },
  };
  render();
  setStatus(`MOTHER! ${loser === 'white' ? 'You lose' : 'AI loses'} — counts double!`);
  scheduleNewGame();
}

function scheduleNewGame() {
  rollBtn.disabled = false;
  rollBtn.textContent = 'New Game';
  rollBtn.onclick = newGame;
}

function newGame() {
  hasMovedThisTurn = false;
  const scores = { ...state.scores };
  state = { ...createInitialState(), scores };
  rollBtn.textContent = 'Roll Dice';
  rollBtn.onclick = null;
  render();
  setStatus('New game — your turn, roll the dice');
}

// ─── Roll button ─────────────────────────────────────────────────────────────
rollBtn.addEventListener('click', async () => {
  if (state.phase !== 'rolling' || state.currentPlayer !== 'white') return;

  // Reine mode: skip dice roll, go straight to piece selection
  if (state.isReine) {
    state = { ...cloneState(state), phase: 'moving' };
    render();
    setStatus('✨ Reine mode! Pick any piece.');
    return;
  }

  rollBtn.disabled = true;
  diceArea.innerHTML = '';

  const values = rollDice();
  const diceEls: HTMLElement[] = values.map(() => {
    const div = document.createElement('div');
    div.className = 'die';
    div.textContent = '?';
    diceArea.appendChild(div);
    return div;
  });

  await animateRoll(diceEls, values);
  callOutDice(values);

  state = {
    ...cloneState(state),
    dice: values,
    diceUsed: values.map(() => false),
    phase: 'moving',
  };

  render();

  if (!hasAnyLegalMove(state)) {
    setStatus('No legal moves — passing turn');
    await delay(1200);
    endHumanTurn();
  } else {
    setStatus('Select a piece to move');
  }
});

// ─── Reine button ─────────────────────────────────────────────────────────────
reineBtn.addEventListener('click', async () => {
  if (state.currentPlayer !== 'white') return;
  if ((state.phase !== 'rolling' && state.phase !== 'moving') || hasMovedThisTurn) return;

  if (state.isReine) {
    state = { ...cloneState(state), isReine: false, reinePickup: null, legalTargets: [], selection: null };
    stopCelebration();
    render();
    setStatus('Select a piece to move');
    return;
  }

  const result = await runQuiz(quizArea, controlsEl, statusBar);
  if (result === 'correct') {
    diceArea.innerHTML = '';
    state = { ...cloneState(state), isReine: true, phase: 'moving', reinePickup: null, legalTargets: [], selection: null };
    startCelebration(celebLayer);
    render();
    setStatus('✨ Reine mode! Pick any piece.');
  } else {
    state = { ...cloneState(state), phase: 'ai' };
    render();
    showBoardOverlay("YOU'RE NOT REINE. Skipping turn.");
    await delay(2500);
    hideBoardOverlay();
    endHumanTurn();
  }
});

// ─── Init ─────────────────────────────────────────────────────────────────────
function delay(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

function showBoardOverlay(text: string): void {
  (boardOverlay.querySelector('span') as HTMLElement).textContent = text;
  boardOverlay.classList.add('visible');
}

function hideBoardOverlay(): void {
  boardOverlay.classList.remove('visible');
}

loadQuestions();
render();
setStatus('Your turn — roll the dice');
